import { useEffect, useRef } from 'react';
import { logEvent, setUserProperties } from 'firebase/analytics';
import { getAnalyticsInstance } from '../firebase';

const LOG_PREFIX = '[Car Hangout Analytics]';
const VISIT_SESSION_KEY = 'ch_page_visit_logged';

const logToDevTools = (eventName, params) => {
  console.groupCollapsed(`${LOG_PREFIX} ${eventName}`);
  // console.log('Event:', eventName);
  // console.log('Params:', params);
  // console.log('Time:', new Date().toLocaleTimeString());
  console.groupEnd();
};

const trackEvent = async (eventName, params = {}) => {
  logToDevTools(eventName, params);

  const analytics = await getAnalyticsInstance();
  if (analytics) {
    logEvent(analytics, eventName, params);
  }
};

const setAnalyticsUserProperties = async (properties) => {
  const analytics = await getAnalyticsInstance();
  if (analytics) {
    setUserProperties(analytics, properties);
  }
};

const getPlatform = () => {
  const ua = navigator.userAgent || '';

  if (/iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
    return 'ios';
  }

  if (/Android/i.test(ua)) {
    return 'android';
  }

  return 'web';
};

const mapReferrerToSource = (hostname) => {
  const host = hostname.toLowerCase();

  if (host.includes('google.')) return 'google';
  if (host.includes('instagram.')) return 'instagram';
  if (host.includes('facebook.') || host === 'fb.com' || host.endsWith('.fb.com')) return 'facebook';
  if (host.includes('twitter.') || host === 't.co' || host.endsWith('.t.co')) return 'twitter';
  if (host.includes('youtube.') || host === 'youtu.be') return 'youtube';
  if (host.includes('tiktok.')) return 'tiktok';
  if (host.includes('linkedin.')) return 'linkedin';
  if (host.includes('bing.')) return 'bing';
  if (host.includes('yahoo.')) return 'yahoo';
  if (host.includes('reddit.')) return 'reddit';
  if (host.includes('pinterest.')) return 'pinterest';

  return hostname;
};

const getTrafficSource = () => {
  const params = new URLSearchParams(window.location.search);
  const utmSource = params.get('utm_source');
  const utmMedium = params.get('utm_medium');
  const utmCampaign = params.get('utm_campaign');

  if (utmSource) {
    return {
      traffic_source: utmSource.toLowerCase(),
      traffic_medium: (utmMedium || 'unknown').toLowerCase(),
      traffic_campaign: utmCampaign || '',
      traffic_type: 'utm',
      referrer: document.referrer || 'none',
    };
  }

  const referrer = document.referrer;

  if (!referrer) {
    return {
      traffic_source: 'direct',
      traffic_medium: 'none',
      traffic_campaign: '',
      traffic_type: 'direct',
      referrer: 'none',
    };
  }

  try {
    const hostname = new URL(referrer).hostname;
    const source = mapReferrerToSource(hostname);

    return {
      traffic_source: source,
      traffic_medium: 'referral',
      traffic_campaign: '',
      traffic_type: 'referrer',
      referrer: hostname,
    };
  } catch {
    return {
      traffic_source: 'unknown',
      traffic_medium: 'referral',
      traffic_campaign: '',
      traffic_type: 'referrer',
      referrer: 'unknown',
    };
  }
};

const getVisitContext = () => ({
  platform: getPlatform(),
  ...getTrafficSource(),
});

const getPageHeight = () =>
  Math.max(
    document.body.scrollHeight,
    document.documentElement.scrollHeight,
    document.body.offsetHeight,
    document.documentElement.offsetHeight
  );

const getScrollTop = () =>
  window.scrollY ||
  document.documentElement.scrollTop ||
  document.body.scrollTop ||
  0;

const getViewedScrollPercent = () => {
  const scrollTop = getScrollTop();
  const pageHeight = getPageHeight();
  const { innerHeight } = window;

  if (pageHeight <= innerHeight) {
    return 0;
  }

  return ((scrollTop + innerHeight) / pageHeight) * 100;
};

export function usePageAnalytics() {
  const pageStartRef = useRef(Date.now());
  const scrolledPast50Ref = useRef(false);
  const timeReportedRef = useRef(false);

  useEffect(() => {
    // console.info(
    //   `${LOG_PREFIX} Tracking active — watching page_visit, scroll_past_50_percent, time_on_page, platform & traffic source`
    // );

    if (!sessionStorage.getItem(VISIT_SESSION_KEY)) {
      sessionStorage.setItem(VISIT_SESSION_KEY, '1');

      const visitContext = getVisitContext();

      setAnalyticsUserProperties({
        user_platform: visitContext.platform,
        last_traffic_source: visitContext.traffic_source,
      });

      trackEvent('page_visit', {
        page_title: document.title,
        page_location: window.location.href,
        ...visitContext,
      });
    }

    let lastLoggedScrollPercent = -1;

    const handleScroll = () => {
      const viewedPercent = getViewedScrollPercent();
      const roundedPercent = Math.round(viewedPercent);

      if (roundedPercent !== lastLoggedScrollPercent && roundedPercent % 10 === 0) {
        lastLoggedScrollPercent = roundedPercent;
        // console.log(`${LOG_PREFIX} scroll progress: ${roundedPercent}%`);
      }

      if (viewedPercent >= 50 && !scrolledPast50Ref.current) {
        scrolledPast50Ref.current = true;

        trackEvent('scroll_past_50_percent', {
          scroll_depth_percent: Math.round(viewedPercent),
        });
      }
    };

    const reportTimeOnPage = (trigger) => {
      if (timeReportedRef.current) {
        return;
      }

      timeReportedRef.current = true;
      const engagementTimeSec = Math.round((Date.now() - pageStartRef.current) / 1000);

      trackEvent('time_on_page', {
        engagement_time_sec: engagementTimeSec,
        exit_trigger: trigger,
      });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        reportTimeOnPage('tab_hidden');
      }
    };

    const handlePageHide = () => {
      reportTimeOnPage('page_hide');
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    window.addEventListener('load', handleScroll);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);

    const scrollCheckInterval = window.setInterval(handleScroll, 1000);
    window.setTimeout(handleScroll, 500);
    window.setTimeout(handleScroll, 2000);

    return () => {
      window.clearInterval(scrollCheckInterval);
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      window.removeEventListener('load', handleScroll);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, []);
}

export const trackStoreClick = (store, location) => {
  const eventName = store === 'app_store' ? 'app_store_click' : 'play_store_click';

  trackEvent(eventName, {
    store,
    click_location: location,
    platform: getPlatform(),
    ...getTrafficSource(),
  });
};

export const trackContactSubmitClick = () => {
  trackEvent('contact_us_submit_click', {
    platform: getPlatform(),
    ...getTrafficSource(),
  });
};

export { trackEvent, getPlatform, getTrafficSource, getVisitContext };
